import { FormattedMessage } from 'react-intl'

import { CartControllerAddResponseErrorTypeEnum } from '@redteclab/api/clients/bully'

import { CartUrl } from '../components/CartIntermediateLabels/CartLink'

import {
  type CartIntermediateDrawerMessage,
  type CartIntermediateDrawerMessages,
  type CartIntermediateMutationResponseData,
} from './CartIntermediateContextValue'

const cartIntermediateAccessorGetErrorType = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): CartControllerAddResponseErrorTypeEnum | undefined => {
  if (Array.isArray(addToCartResponseData)) {
    return addToCartResponseData[0]?.errorType
  }

  return addToCartResponseData?.errorType
}

const cartIntermediateAccessorGetResponseHasError = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): boolean => {
  if (Array.isArray(addToCartResponseData)) {
    return addToCartResponseData.some((response) => response.hasError)
  }

  return Boolean(!addToCartResponseData || addToCartResponseData.hasError)
}

const cartIntermediateAccessorGetInfoMessage = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): CartIntermediateDrawerMessage | null => {
  const errorType = cartIntermediateAccessorGetErrorType(addToCartResponseData)

  if (!addToCartResponseData && !errorType) {
    return {
      message: <FormattedMessage id="cart.notification.missingOrderDraft" />,
      severity: 'error',
    }
  }

  switch (errorType) {
    case CartControllerAddResponseErrorTypeEnum.PutMpEntryToCartWithOds: {
      return {
        message: (
          <FormattedMessage
            id="cart.intermediate.cantMixMiraklAndODSProducts"
            values={{ link: <CartUrl /> }}
          />
        ),
        severity: 'error',
      }
    }
    case CartControllerAddResponseErrorTypeEnum.PutMpEntryToErxCardWithErx: {
      return {
        message: (
          <FormattedMessage
            id="cart.intermediate.cantMixMiraklAndERXProducts"
            values={{ link: <CartUrl /> }}
          />
        ),
        severity: 'error',
      }
    }
    case CartControllerAddResponseErrorTypeEnum.PutRxEntryToCartWithErx: {
      return {
        message: (
          <FormattedMessage id="cart.intermediate.cantMixRXAndERXProducts" />
        ),
        severity: 'error',
      }
    }
    default: {
      return null
    }
  }
}

const cartIntermediateAccessorGetTitleMessage = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): CartIntermediateDrawerMessage | null => {
  const isError = cartIntermediateAccessorGetResponseHasError(
    addToCartResponseData,
  )
  const titleMessage =
    !addToCartResponseData || isError
      ? 'cartintermediate.drawer.error.title'
      : 'cart.notification.addedItem'

  switch (titleMessage) {
    case 'cart.notification.addedItem': {
      return {
        message: <FormattedMessage id="cart.notification.addedItem" />,
        severity: 'success',
      }
    }
    case 'cartintermediate.drawer.error.title': {
      return {
        message: <FormattedMessage id="cartintermediate.drawer.error.title" />,
        severity: 'error',
      }
    }
    default: {
      return null
    }
  }
}

export const cartIntermediateAccessorGetIsDataSuccessfullyAddedToCart = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): boolean => {
  return Boolean(
    addToCartResponseData &&
      !cartIntermediateAccessorGetResponseHasError(addToCartResponseData),
  )
}

export const cartIntermediateAccessorGetDrawerMessages = (
  addToCartResponseData: CartIntermediateMutationResponseData,
): CartIntermediateDrawerMessages => {
  const titleMessage = cartIntermediateAccessorGetTitleMessage(
    addToCartResponseData,
  )
  const infoMessage = cartIntermediateAccessorGetInfoMessage(
    addToCartResponseData,
  )

  return { infoMessage, titleMessage }
}
